/**
 * Site Access Guard Component
 * 
 * Main access control layer that enforces site access modes:
 * - open: Normal access, public pages for anonymous
 * - authenticated_only: All pages require login
 * - read_only: Everyone can view, no writes (except admin for owner/admin)
 * - maintenance: Only owner/admin can access (full access after login)
 * 
 * Hierarchy: Site Access Mode > User Role > Page Type
 */

import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useConfig } from '@/contexts/ConfigContext';
import { useAuth } from '@/contexts/AuthContext';
import MaintenancePage from '@/pages/MaintenancePage';
import ReadOnlyBanner from '@/components/ReadOnlyBanner';

// Paths that are ALWAYS accessible regardless of mode
const ALWAYS_ACCESSIBLE = [
  '/login',
  '/auth',
  '/registar',           // Needed for invite registration
  '/esqueci-password',
  '/repor-password', 
  '/verificar-email',
  '/confirmar-email',
  '/aguardar-aprovacao',
  '/termos',
  '/privacidade',
  '/export/',            // Export download page (token-based access)
  '/confirmar-fecho-conta', // Account closure confirmation (token-based)
  '/cancelar-fecho-conta',  // Account closure cancellation (token-based)
  '/fecho-cancelado',       // Account closure cancelled landing page
  '/planos',               // Pricing page - always public
];

// Paths accessible to anonymous in open/read_only modes
const PUBLIC_PATHS = [
  '/comunidade',
  '/passeios',
  '/passeio/',
  '/clubes',
  '/clube/',
  // Legacy routes
  '/grupos', 
  '/grupo/',
  '/regista-te',
  '/registar',
  '/sobre',
  '/faq',
  '/ajuda',
  '/contactos',
  '/confirmar-fecho-conta',
  '/cancelar-fecho-conta',
  '/fecho-cancelado',
];

export default function SiteAccessGuard({ children }) {
  const { siteAccess, loaded } = useConfig();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const mode = siteAccess?.mode || 'open';
  const path = location.pathname;
  
  // CRITICAL: Allow OAuth callback processing before any access checks
  // The #session_id in the URL indicates Google OAuth is returning
  // We must let this through so AuthCallback can process it
  const isOAuthCallback = location.hash?.includes('session_id=');
  
  // User role checks
  const isAuthenticated = !!user;
  const isOwner = user?.role === 'owner';
  const isAdmin = user?.role === 'admin';
  const isOwnerOrAdmin = isOwner || isAdmin;
  
  // Path type checks
  const isAlwaysAccessible = ALWAYS_ACCESSIBLE.some(p => path.startsWith(p));
  const isPublicPath = PUBLIC_PATHS.some(p => path === p || path.startsWith(p + '/') || (p.endsWith('/') && path.startsWith(p)));
  const isAdminPath = path.startsWith('/admin');

  // Handle redirects in useEffect (side effects)
  useEffect(() => {
    if (!loaded || authLoading) return;

    // Admin path - non-admin redirect to login
    if (isAdminPath && !isOwnerOrAdmin) {
      navigate('/login', { replace: true });
      return;
    }

    // Authenticated only mode - redirect anonymous to login
    if (mode === 'authenticated_only' && !isAuthenticated && !isAlwaysAccessible) {
      navigate('/login', { 
        state: { from: path },
        replace: true 
      });
    }
  }, [mode, loaded, authLoading, path, isAlwaysAccessible, isAdminPath, isOwnerOrAdmin, isAuthenticated, navigate]);

  // Still loading configs or auth
  if (!loaded || authLoading) {
    return null;
  }

  // CRITICAL: Allow OAuth callback through - don't block with maintenance page
  // The AppRouter will detect #session_id and show AuthCallback
  if (isOAuthCallback) {
    return children;
  }

  // Maintenance mode - show maintenance page to non-admin users
  // Owner/admin have full access after login
  if (mode === 'maintenance' && !isOwnerOrAdmin && !isAlwaysAccessible) {
    return <MaintenancePage />;
  }

  // Read-only mode - show banner (write blocking is done in backend)
  if (mode === 'read_only' && !isAdminPath) {
    return (
      <>
        <ReadOnlyBanner message={siteAccess?.read_only_message} />
        {children}
      </>
    );
  }

  return children;
}

/**
 * Hook to check if writes are allowed in current mode
 * Use this in components that need to disable edit buttons
 */
export function useCanWrite() {
  const { siteAccess } = useConfig();
  const { user } = useAuth();
  
  const mode = siteAccess?.mode || 'open';
  const isOwnerOrAdmin = user?.role === 'owner' || user?.role === 'admin';
  
  // In read_only mode, only owner/admin can write (in admin area)
  if (mode === 'read_only') {
    return isOwnerOrAdmin;
  }
  
  // In maintenance, only owner/admin
  if (mode === 'maintenance') {
    return isOwnerOrAdmin;
  }
  
  // Open or authenticated_only - normal rules
  return !!user;
}
